import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Settings, MessageSquare } from 'lucide-react';
import config from '../../config';
import { useComponentMetrics, getMetricObjectiveClassName, getCpaClassName, getCosClassName, getMetricLabel } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../../utils/variationUtils';
import { getDateRangeFromPeriod, calculatePreviousPeriod as calculateComparisonPeriod } from '../../utils/periodUtils';
const API_URL = config.API_URL;

const EngagementDetailsTable = ({
  token,
  selectedAccount,
  period,
  customStartDate,
  customEndDate,
  isVisible,
  onToggleVisibility,
  formatNumber,
  selectedCampaignFilter,
  labelCampaignIds,
  selectedClientId,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'fbads_engagement'
}) => {
  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('fbads_engagement', selectedAccount || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Helper pour obtenir le label d'une métrique (avec labels personnalisés)
  const getLabel = useCallback((metricKey) => {
    return getMetricLabel(metricKey, moduleId, selectedAccount || 'global');
  }, [moduleId, selectedAccount]);

  // Fonctions de formatage locales
  const formatNumberLocal2 = (num) => new Intl.NumberFormat('fr-FR').format(Math.round(num || 0));
  const formatCurrencyLocal = (num) => `€${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)}`;
  const formatPercentageLocal = (num) => `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)}%`;

  // Configuration des métriques pour le rendu dynamique (métriques spécifiques aux engagements)
  const metricConfig = useMemo(() => ({
    engagements: { sortKey: 'engagements', defaultLabel: 'Engagements' },
    cout: { sortKey: 'spend', defaultLabel: 'Dépenses' },
    cout_par_engagement: { sortKey: 'costPerEngagement', defaultLabel: 'Coût/Engagement' }
  }), []);





  const fetchData = useCallback(async () => {
    if (!selectedAccount || !token) return;

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        accountId: selectedAccount,
        period,
        customStartDate,
        customEndDate,
        campaignFilter: selectedCampaignFilter,
        labelCampaignIds,
        clientId: selectedClientId
      };

      const response = await fetch(`${API_URL}/api/facebook-ads/engagement-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
        // Fermer toutes les catégories par défaut
        setExpandedCategories({});

        // Récupérer les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          // Déterminer les dates de la période actuelle
          let currentStart = customStartDate;
          let currentEnd = customEndDate;

          // Si pas de dates personnalisées, calculer à partir de la période
          if (!currentStart || !currentEnd) {
            const dateRange = getDateRangeFromPeriod(period);
            currentStart = dateRange.startDate;
            currentEnd = dateRange.endDate;
          }

          const comparisonDates = calculateComparisonPeriod(currentStart, currentEnd, comparisonType);
          console.log('💬 [ENGAGEMENT-COMPARE] Récupération période précédente:', comparisonDates);

          const prevResponse = await fetch(`${API_URL}/api/facebook-ads/engagement-details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              ...requestBody,
              period: 'CUSTOM',
              customStartDate: comparisonDates.startDate,
              customEndDate: comparisonDates.endDate
            })
          });

          const prevResult = await prevResponse.json();
          if (prevResult.success) {
            setPreviousData(prevResult.data);
            console.log('💬 [ENGAGEMENT-COMPARE] Données précédentes récupérées');
          }
        } else {
          setPreviousData(null);
        }
      } else {
        setError(result.error || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error('Erreur fetch engagement details:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, token, period, customStartDate, customEndDate, selectedCampaignFilter, labelCampaignIds, selectedClientId, compareEnabled, comparisonType]);

  useEffect(() => {
    if (isVisible) {
      fetchData();
    }
  }, [fetchData, isVisible, compareEnabled, comparisonType]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const renderCategorySection = (category) => {
    const isExpanded = expandedCategories[category.category];

    // Trouver la catégorie correspondante dans les données précédentes
    const previousCategory = previousData?.engagements?.find(c => c.category === category.category);

    return (
      <div
        key={category.category}
        style={{
          marginBottom: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        {/* Header de catégorie */}
        <div
          onClick={() => toggleCategory(category.category)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: category.color + '15',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.2em' }}>{category.icon}</span>
            <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.95em' }}>
              {category.title}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'right' }}>
              <span style={{
                color: category.color,
                fontWeight: '700',
                fontSize: '1em'
              }}>
                {formatNumber(category.total)}
              </span>
              {previousCategory && renderVariation(calculateVariation(category.total, previousCategory.total))}
            </div>
            <span style={{
              color: '#6b7280',
              fontSize: '0.85em',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}>
              ▼
            </span>
          </div>
        </div>

        {/* Contenu de la catégorie */}
        {isExpanded && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{
                  padding: '10px 16px',
                  textAlign: 'left',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  color: '#6b7280',
                  borderBottom: '1px solid #e5e7eb',
                  width: '35%'
                }}>
                  Type
                </th>
                <th style={{
                  padding: '10px 16px',
                  textAlign: 'left',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  color: '#6b7280',
                  borderBottom: '1px solid #e5e7eb',
                  width: '30%'
                }}>
                  Description
                </th>
                <th style={{
                  padding: '10px 16px',
                  textAlign: 'right',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  color: '#6b7280',
                  borderBottom: '1px solid #e5e7eb',
                  width: '15%'
                }}>
                  Nombre
                </th>
                <th style={{
                  padding: '10px 16px',
                  textAlign: 'right',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  color: '#6b7280',
                  borderBottom: '1px solid #e5e7eb',
                  width: '20%'
                }}>
                  Coût unitaire
                </th>
              </tr>
            </thead>
            <tbody>
              {category.items.map((item, index) => {
                // Trouver l'item correspondant dans la catégorie précédente
                const previousItem = previousCategory?.items?.find(i => i.type === item.type);

                return (
                  <tr
                    key={item.type}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                      borderBottom: '1px solid #f3f4f6'
                    }}
                  >
                    <td style={{
                      padding: '10px 16px',
                      fontSize: '0.9em',
                      color: '#374151'
                    }}>
                      <span style={{ marginRight: '8px' }}>{item.icon}</span>
                      <span style={{ fontWeight: item.isTotal ? '600' : '500' }}>{item.label}</span>
                      {item.isTotal && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '0.75em',
                          color: '#9ca3af',
                          fontWeight: 'normal'
                        }}>
                          (total)
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '10px 16px',
                      fontSize: '0.8em',
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      {item.description}
                    </td>
                    <td style={{
                      padding: '10px 16px',
                      textAlign: 'right',
                      fontSize: '0.9em',
                      fontWeight: item.isTotal ? '700' : '600',
                      color: category.color
                    }}>
                      {formatNumber(item.value)}
                      {previousItem && renderVariation(calculateVariation(item.value, previousItem.value))}
                    </td>
                    <td style={{
                      padding: '10px 16px',
                      textAlign: 'right',
                      fontSize: '0.9em',
                      color: '#6b7280'
                    }}>
                      {item.costPer >= 0 ? `${formatNumber(item.costPer, 2)} €` : '-'}
                      {previousItem &&
                        renderVariation(calculateVariation(item.costPer, previousItem.costPer), true)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // Ne pas afficher le composant si erreur, chargement, pas de données, ou si tous les engagements sont à 0
  const hasValidData = data && data.totals && data.totals.engagements > 0;

  if (loading || error || !hasValidData) {
    return null;
  }

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="engagement-details-section" style={{
      marginTop: '24px',
      background: '#FAF9F5',
      borderRadius: '12px',
      padding: '24px'
    }}>
      <div
        className="section-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isVisible ? '16px' : '0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h4 style={{ margin: 0, fontSize: '1.1em', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={18} />
            Engagements
          </h4>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Engagements"
            />
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && isVisible && (
        <div style={{
          padding: '0'
        }}>
          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              color: '#6b7280'
            }}>
              <div className="spinner-border" role="status" style={{ marginRight: '10px' }} />
              Chargement des engagements...
            </div>
          )}

          {error && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              color: '#dc2626',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && data && data.engagements && (
            <div>
              {/* Résumé global */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '20px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  flex: '1',
                  minWidth: '150px',
                  padding: '16px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#166534', marginBottom: '4px' }}>
                    Total Engagements
                  </div>
                  <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#15803d' }}>
                    {formatNumber(data.totals.engagements)}
                    {previousData?.totals && renderVariation(calculateVariation(data.totals.engagements, previousData.totals.engagements))}
                  </div>
                </div>
                <div style={{
                  flex: '1',
                  minWidth: '150px',
                  padding: '16px',
                  backgroundColor: '#fefce8',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #fef08a'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#854d0e', marginBottom: '4px' }}>
                    Catégories actives
                  </div>
                  <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#a16207' }}>
                    {data.engagements.length}
                  </div>
                </div>
                <div style={{
                  flex: '1',
                  minWidth: '150px',
                  padding: '16px',
                  backgroundColor: '#FAF9F5',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #bfdbfe'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#1e40af', marginBottom: '4px' }}>
                    Dépenses totales
                  </div>
                  <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1d4ed8' }}>
                    {formatNumber(data.totals.spend, 2)} €
                    {previousData?.totals && renderVariation(calculateVariation(data.totals.spend, previousData.totals.spend), true)}
                  </div>
                </div>
                <div style={{
                  flex: '1',
                  minWidth: '150px',
                  padding: '16px',
                  backgroundColor: '#fdf4ff',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #f5d0fe'
                }}>
                  <div style={{ fontSize: '0.85em', color: '#86198f', marginBottom: '4px' }}>
                    Coût par engagement
                  </div>
                  <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#a21caf' }}>
                    {data.totals.engagements > 0
                      ? `${formatNumber(data.totals.spend / data.totals.engagements, 2)} €`
                      : '-'
                    }
                    {previousData?.totals &&
                      renderVariation(
                        calculateVariation(
                          data.totals.spend / data.totals.engagements,
                          previousData.totals.spend / previousData.totals.engagements
                        ),
                        true // inversé: une baisse du coût est positive
                      )
                    }
                  </div>
                </div>
              </div>

              {/* Catégories d'engagement */}
              <div>
                {data.engagements.map(category => renderCategorySection(category))}
              </div>
            </div>
          )}

          {!loading && !error && (!data || !data.engagements || data.engagements.length === 0) && (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              Aucune donnée d'engagement disponible pour cette période
            </div>
          )}
        </div>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="fbads_engagement"
          accountId={selectedAccount || 'global'}
          title="Engagement Meta Ads"
          hideApplyToAll={true}
        />
      )}
    </div>
  );
};

export default EngagementDetailsTable;
